import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { FearGreedData } from "@/lib/fear-greed";
import { useCallback } from "react";

const LS_KEY = "fng-latest-cache";

function readLocalStorage(): FearGreedData | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FearGreedData;
  } catch {
    return null;
  }
}

function writeLocalStorage(data: FearGreedData) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch { /* storage full — non-critical */ }
}

async function fetchLatest(): Promise<FearGreedData> {
  const { data, error } = await supabase.functions.invoke("fng-latest");
  if (error) {
    const cached = readLocalStorage();
    if (cached) return cached;
    throw error;
  }
  const result = data as FearGreedData;
  writeLocalStorage(result);
  return result;
}

export function useFearGreed() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["fng-latest"],
    queryFn: fetchLatest,
    staleTime: 15 * 60 * 1000, // 15 min — data is daily, no need to poll
    refetchOnWindowFocus: false,
    placeholderData: () => readLocalStorage() ?? undefined,
  });

  // Detect stale data — from payload flag or from error fallback
  const isStale = query.data?.stale === true || (query.isError && !!query.data);

  // Tap = ritual only, no upstream fetch. Just returns false.
  const tapButton = useCallback(async (): Promise<{ didFetch: boolean }> => {
    return { didFetch: false };
  }, []);

  // Long-press force refresh — re-reads cache (scheduler keeps it fresh)
  const forceRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["fng-latest"] });
  }, [queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading && !query.data,
    error: query.error,
    isFetching: query.isFetching,
    isStale,
    tapButton,
    forceRefresh,
  };
}
